import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { X } from 'lucide-react';
import { CreateDealerDTO } from '../../../services/api/endpoints/dealer.endpoint';

interface DealerFormProps {
  onSubmit: (data: CreateDealerDTO) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
}

export default function DealerForm({ onSubmit, onClose, isLoading = false }: DealerFormProps) {
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateDealerDTO>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobileNo: '',
      promoCode: '',
    },
  });

  const onFormSubmit = async (data: CreateDealerDTO) => {
    await onSubmit(data);
  };

  return (
    <Box sx={{ width: { xs: '100%', sm: 450 }, p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Create Dealer
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={isLoading}>
          <X size={18} />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2} columns={12}>
          <Grid item xs={6}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  disabled={isLoading}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  disabled={isLoading}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="mobileNo"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Phone (Optional)" fullWidth disabled={isLoading} />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="promoCode"
              control={control}
              rules={{
                required: 'Promo code is required',
                pattern: {
                  value: /^[A-Za-z0-9_-]+$/,
                  message: 'Promo code can only contain letters, numbers, hyphens, and underscores',
                },
                minLength: {
                  value: 3,
                  message: 'Promo code must be at least 3 characters',
                },
                maxLength: {
                  value: 20,
                  message: 'Promo code must be at most 20 characters',
                },
              }}
              render={({ field: { onChange, ...field } }) => (
                <TextField
                  {...field}
                  onChange={e => onChange(e.target.value.toUpperCase())}
                  label="Promo Code"
                  fullWidth
                  error={!!errors.promoCode}
                  helperText={
                    errors.promoCode?.message ||
                    'Unique code used to link organizations to this dealer'
                  }
                  placeholder="e.g., DEALER-ABC"
                  disabled={isLoading}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isLoading ? 'Creating...' : 'Create Dealer'}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
